import React from "react";
import { AllPagesToggle } from "@/components/traffic/AllPagesToggle";
import { CoverageLine, reachesNoBuyer } from "@/components/traffic/CoverageLine";

const KIND_LABEL = { shared: "Shared page", buyer: "Buyer · branded links", own: "Buyer's own page" };
const isLive = (p) => p.status === "published" && !p.is_test;

function PageCell({ p, count }) {
  const dead = count > 0 && reachesNoBuyer(p.coverage);
  return (
    <th scope="row" className={`sticky left-0 z-10 border-r-2 border-black px-3 py-2 text-left font-normal min-w-[150px] sm:min-w-[240px] max-w-[320px] ${dead ? "bg-red-50" : count === 0 && isLive(p) ? "bg-amber-50" : "bg-white"}`}>
      <div className={`text-[12px] font-bold ${p.kind === "own" ? "pl-3" : ""}`}>{p.title}</div>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">
        {KIND_LABEL[p.kind]} · {p.status}{p.is_test ? " · test" : ""} · <span className="font-mono normal-case tracking-normal">{p.path}</span>
      </div>
      {count === 0 && isLive(p) && <div className="text-[10px] font-bold text-amber-800" data-testid={`traffic-row-none-${p.id}`}>No one can send traffic here yet</div>}
      <CoverageLine cov={p.coverage} testId={`traffic-coverage-${p.id}`} />
    </th>
  );
}

function Cell({ p, partner, onToggle, busyKey }) {
  const key = `${partner.id}:${p.id}`;
  const auto = partner.all_pages && isLive(p);
  const on = auto || partner.page_ids.includes(p.id);
  return (
    <td className={`border-l border-zinc-200 text-center ${on ? "bg-emerald-50" : ""}`}>
      <input type="checkbox" checked={on} disabled={auto || busyKey === key} onChange={() => onToggle(partner, p, !on)}
             title={auto ? "Included by All pages" : on ? "Can send traffic here" : "Can't send traffic here"}
             data-testid={`traffic-cell-${partner.id}-${p.id}`} className="w-4 h-4 accent-black align-middle" />
      {auto && <div className="text-[8px] uppercase tracking-widest text-emerald-800">auto</div>}
    </td>
  );
}

export function AccessMatrix({ pages, partners, onToggle, onAllPages, busyKey }) {
  const counts = Object.fromEntries(pages.map((p) => [p.id, partners.filter((m) => (m.all_pages && isLive(p)) || m.page_ids.includes(p.id)).length]));
  const dead = (m) => pages.filter((p) => ((m.all_pages && isLive(p)) || m.page_ids.includes(p.id)) && reachesNoBuyer(p.coverage)).length;
  return (
    <div className="overflow-x-auto border-2 border-black bg-white" data-testid="traffic-matrix">
      <table className="text-xs border-collapse">
        <thead>
          <tr className="border-b-2 border-black">
            <th className="sticky left-0 z-20 bg-zinc-100 border-r-2 border-black px-3 py-2 text-left label-mono">Page</th>
            {partners.map((m) => (
              <th key={m.id} className="border-l border-zinc-200 bg-zinc-100 px-2 py-2 align-bottom min-w-[120px]" data-testid={`traffic-col-${m.id}`}>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">{m.kind === "media_buyer" ? "Media buyer" : "Affiliate"}</div>
                <div className="text-[11px] font-bold leading-tight">{m.name}</div>
                <div className="font-mono text-[9px] text-zinc-500">{m.code}</div>
                {dead(m) > 0 && (
                  <div className="mt-1 text-[9px] font-bold uppercase tracking-widest text-red-700" data-testid={`traffic-col-dead-${m.id}`}>
                    {dead(m) === 1 ? "1 page reaches" : `${dead(m)} pages reach`} no buyer
                  </div>
                )}
                <div className="mt-1"><AllPagesToggle kind={m.kind} id={m.id} value={m.all_pages} compact onChange={(d) => onAllPages(m, d)} testId={`traffic-all-${m.id}`} /></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pages.map((p) => (
            <tr key={p.id} className={`border-b border-zinc-200 ${p.kind === "buyer" ? "border-t-2 border-t-black" : ""}`} data-testid={`traffic-row-${p.id}`}>
              <PageCell p={p} count={counts[p.id]} />
              {partners.map((m) => <Cell key={m.id} p={p} partner={m} onToggle={onToggle} busyKey={busyKey} />)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
